import { useState } from 'react';
import { Cell, formatRejectedOn, rejectReasonLabel, type DashboardRow } from './triageDeskShared';

/**
 * SUN-233 — the Lead Triage "Rejected" tab table. Same shell as the other tabs
 * (filters, scroll, paging); rows are leads marked Irrelevant/rejected, newest
 * rejection first. Restore asks first, then the lead goes back to Unassigned.
 */
export function RejectedTable({ rows, loading, pendingIds, canRestore, onRestore }: {
  rows: DashboardRow[];
  loading: boolean;
  pendingIds: ReadonlySet<string>;
  /** From the server: only a viewer the restore route would allow sees the button. */
  canRestore: boolean;
  onRestore: (row: DashboardRow) => void;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const headings = ['Lead', 'Requirement', 'Rejected on', 'Reason', 'Rejected by', ...(canRestore ? ['Action'] : [])];
  return (
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          {headings.map((h) => (
            <th key={h} className="text-left px-3 py-2 font-semibold text-gray-700">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {loading && Array.from({ length: 4 }).map((_, i) => (
          <tr key={i} className="border-b border-gray-100 animate-pulse">
            {headings.map((h) => (
              <td key={h} className="px-3 py-3"><div className="h-3 bg-gray-100 rounded w-20" /></td>
            ))}
          </tr>
        ))}
        {!loading && rows.length === 0 && (
          <tr>
            <td colSpan={headings.length} className="px-3 py-12 text-center text-sm text-gray-500">
              No rejected leads found. Leads marked Irrelevant show up here.
            </td>
          </tr>
        )}
        {!loading && rows.map((row) => {
          const pending = pendingIds.has(row.id);
          const confirming = confirmId === row.id;
          return (
            <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="px-3 py-2">
                <div className="font-medium text-gray-900"><Cell value={row.name} empty="dash" /></div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {[row.phone, row.location].filter(Boolean).join(' · ') || '—'}
                </div>
              </td>
              <td className="px-3 py-2"><Cell value={row.requirement} empty="dash" /></td>
              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                {row.rejectedAt ? <time dateTime={row.rejectedAt}>{formatRejectedOn(row.rejectedAt)}</time> : <Cell value={null} empty="dash" />}
              </td>
              <td className="px-3 py-2">
                <span className="inline-flex items-center text-xs font-medium text-gray-700 bg-gray-100 rounded px-2 py-0.5 whitespace-nowrap">
                  {rejectReasonLabel(row.rejectReason)}
                </span>
              </td>
              <td className="px-3 py-2"><Cell value={row.rejectedByName} empty="dash" /></td>
              {canRestore && <td className="px-3 py-2">
                {confirming ? (
                  <div className="flex flex-col items-start gap-1.5" role="group" aria-label="Confirm restore">
                    <span className="text-xs text-gray-700">Restore this lead to Unassigned?</span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        disabled={pending}
                        onClick={() => { setConfirmId(null); onRestore(row); }}
                        className="text-xs border border-green-600 bg-green-600 text-white rounded px-2 py-1 font-medium hover:bg-green-700 disabled:opacity-50 disabled:pointer-events-none"
                      >Yes, restore</button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(null)}
                        className="text-xs border border-gray-300 bg-white text-gray-700 rounded px-2 py-1 hover:bg-gray-50"
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={pending}
                    onClick={() => setConfirmId(row.id)}
                    className="text-xs border border-gray-300 rounded px-2 py-1 bg-white hover:border-green-500 hover:bg-green-50 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap"
                  >{pending ? 'Restoring…' : 'Restore'}</button>
                )}
              </td>}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
